/**
 * ArdDetector — Aggression-Response Divergence (STRIKE_SPEC.md, Phase 1)
 * ─────────────────────────────────────────────────────────────────────
 * Levi's read, formalized: a LARGE aggressive event (relative to the
 * active tape) whose price response CONTRADICTS the aggressor's
 * direction. Sell aggression absorbed at value + instant markup = long
 * entry. Buyer aggression that price falls on at the highs = protect.
 *
 * PHASE 1 = SHADOW: this class never trades. It observes a slot's swap
 * stream and emits would-fire / decline events through callbacks. Every
 * decline names its gate in the STRIKE vocabulary so tuning is done on
 * evidence, exactly like BASE_DISQUALIFIED did for the base engine.
 *
 * Dependency-free by design (runs on the droplet next to bot_runner and
 * in the browser). Field-mismatch-immune: swap volume reads
 * (sol ?? amount) and price reads (mcap ?? tradeMcap) — the exact
 * two-name split that silently zeroed the base engine's churn on the
 * server.
 *
 * G0 (scope) is implicit: the detector only exists inside coordinator
 * slots, i.e. only on coins the bot picked.
 *
 * ── SIZE / VOLUME ──────────────────────────────────────────────────
 * The whole engine is size-aware but RELATIVE: an event qualifies at
 * eventSizeX × the active-window MEDIAN swap, so the bar self-scales
 * from a dust coin to a major. Volume is measured in SOL notional
 * (size × price), and the G2 value nodes are volume-weighted (the HVN
 * histogram buckets by SOL). What Phase 1 now ALSO carries — so the
 * shadow stats can weight by it later — is the event's magnitude BEYOND
 * the threshold: strengthX (the ×median multiple) and sol (absolute
 * notional) ride every would-fire. It does NOT measure the absorbing
 * side's depth (server-side has no book; DLMM bins are the wall-ahead
 * proxy only) — absorption is inferred from price HOLDING, not from the
 * bid that held it.
 */

export class ArdDetector {
  constructor(config = {}) {
    this.config = {
      // ── G1 ACTIVE + VOLATILE ──
      activeWindowSec:    config.activeWindowSec    ?? 60,
      minSwapsPerSec:     config.minSwapsPerSec     ?? 1.0,
      volWindowSec:       config.volWindowSec       ?? 120,
      minRangePct:        config.minRangePct        ?? 0.02,   // 2% realized range — "volatility is important"
      // ── G2 AT_VALUE ──
      hvnTopFrac:         config.hvnTopFrac         ?? 0.25,   // top 25% volume buckets = value nodes
      valueBandPct:       config.valueBandPct       ?? 0.006,  // within 0.6% of a node
      profileWindowSec:   config.profileWindowSec   ?? 900,    // rolling 15min swap-histogram profile
      requireValueNode:   config.requireValueNode   ?? false,  // Phase 1: OPEN (learn); flip true for Phase 2
      // ── G3 AGGRESSION_EVENT ──
      eventSizeX:         config.eventSizeX         ?? 25,     // single print ≥ 25× active median
      minEventSol:        config.minEventSol        ?? 1.0,    // absolute floor
      clusterWindowSec:   config.clusterWindowSec   ?? 2,
      clusterSizeX:       config.clusterSizeX       ?? 40,     // cluster net ≥ 40× median
      // ── G4 DIVERGENT_RESPONSE ──
      responseWindowSec:  config.responseWindowSec  ?? 6,
      absorbBreakPct:     config.absorbBreakPct     ?? 0.004,  // >0.4% below event low = absorption failed
      responseMovePct:    config.responseMovePct    ?? 0.006,  // ≥0.6% markup from event price
      responseVelPctPerSec: config.responseVelPctPerSec ?? 0.0015, // ≥0.15%/s — speed is a hard gate
      // ── PASS / PROTECT ──
      headroomPct:        config.headroomPct        ?? 0.015,  // wall within 1.5% above = RESISTANCE_AHEAD
      // ── de-dup housekeeping ──
      dedupKeepMs:        config.dedupKeepMs        ?? 12_000, // how long a fired/declined key blocks a repeat
      ...config,
    }
    this.onWouldFire = null      // ({ dir, token, mcap, event, gates }) — Phase 1: log/ping only
    this.onDecline   = null      // ({ reason, token, detail })
    this._pending = []           // open aggression events awaiting response
    this._lastSwapTs = 0         // consume-new-only cursor (by TIME, not index)
    this._seenKeys = new Map()   // key -> t0 : blocks BOTH event re-open AND decline spam
  }

  reset() { this._pending = []; this._lastSwapTs = 0; this._seenKeys.clear() }

  // Field-mismatch-immune accessors
  static vol(sw)  { return Number(sw.sol ?? sw.amount) || 0 }
  static px(sw)   { return Number(sw.mcap ?? sw.tradeMcap) || 0 }
  static t(sw)    { return Number(sw.time) || 0 }

  // wallAbove may arrive as a bare mcap number OR as OrderflowBot's
  // { mcap, notionalSol, distancePct } object. Normalize to a number so
  // `wall > 0` can never be silently false against an object (which was the
  // integration trap that made RESISTANCE_AHEAD never fire).
  static wallMcap(w) {
    if (w == null) return 0
    if (typeof w === 'number') return w
    return Number(w.mcap) || 0
  }

  /**
   * update(ctx) — call once per tick.
   * ctx: { swaps, mcap, token, wallAbove }
   *   swaps: the slot's rolling raw-swap window (chronological)
   *   mcap: current highlight mcap
   *   wallAbove: nearest DLMM ask-wall — number OR {mcap,...}, or null
   */
  update(ctx) {
    const { swaps, mcap, token } = ctx
    if (!Array.isArray(swaps) || swaps.length === 0 || !(mcap > 0)) return
    // TAPE CLOCK: ctx.now = server-aligned time (browser passes serverNow so a
    // skewed local clock can't shift the window off the trade timestamps).
    // Absent (droplet runner) → Date.now(), which is NTP-synced there.
    const now = Number.isFinite(ctx.now) ? ctx.now : Date.now()
    const V = ArdDetector.vol, P = ArdDetector.px, T = ArdDetector.t
    const cfg = this.config

    // Prune the de-dup memory so it can't grow unbounded on a long-lived slot.
    if (this._seenKeys.size > 64) {
      const cut = now - cfg.dedupKeepMs
      for (const [k, t] of this._seenKeys) if (t < cut) this._seenKeys.delete(k)
    }

    // The slot's window array gets rebuilt/shifted; a raw index cursor breaks
    // on shift. Cursor by TIME instead: only consume swaps newer than the
    // last consumed timestamp.
    const newSwaps = []
    for (let i = swaps.length - 1; i >= 0; i--) {
      if (T(swaps[i]) <= this._lastSwapTs) break
      newSwaps.push(swaps[i])
    }
    newSwaps.reverse()
    if (newSwaps.length) this._lastSwapTs = T(newSwaps[newSwaps.length - 1])

    // ── G1: ACTIVE + VOLATILE (computed over the window every tick) ──
    const actCut = now - cfg.activeWindowSec * 1000
    const volCut = now - cfg.volWindowSec * 1000
    let actCount = 0, sizes = []
    let rMin = Infinity, rMax = -Infinity
    for (let i = swaps.length - 1; i >= 0; i--) {
      const ts = T(swaps[i])
      if (ts < volCut) break
      const p = P(swaps[i])
      if (p > 0) { if (p < rMin) rMin = p; if (p > rMax) rMax = p }
      if (ts >= actCut) { actCount++; const v = V(swaps[i]); if (v > 0) sizes.push(v) }
    }
    const swapsPerSec = actCount / cfg.activeWindowSec
    const rangePct = (rMin < Infinity && rMax > 0) ? (rMax - rMin) / rMax : 0
    const active = swapsPerSec >= cfg.minSwapsPerSec
    const volatile = rangePct >= cfg.minRangePct
    sizes.sort((a, b) => a - b)
    const median = sizes.length ? sizes[Math.floor(sizes.length / 2)] : 0

    // Stash gate context BEFORE the event scan below — _openEvent reads
    // this._g1/_valueNodes, so these must be current-tick before any event
    // can open. (The smoke test caught the original after-scan ordering:
    // every event declined NOT_ACTIVE off an undefined _g1.)
    this._g1 = { active, volatile, swapsPerSec: +swapsPerSec.toFixed(2), rangePct: +(rangePct * 100).toFixed(2), median }
    this._valueNodes = this._buildProfile(swaps, now, mcap)

    // ── G3: scan NEW swaps for sell-aggression events (long side) and
    //        buy-aggression (mirror, for PROTECT warnings) ──
    if (active && median > 0) {
      // (a) single outlier prints
      for (const sw of newSwaps) {
        const v = V(sw)
        if (v < cfg.minEventSol) continue
        if (v >= cfg.eventSizeX * median) this._openEvent(sw.side, P(sw) || mcap, v, T(sw) || now, token, ctx, { kind: 'print', x: +(v / median).toFixed(1) })
      }
      // (b) cluster: net same-side flow in the trailing cluster window.
      //     cLastPx captures the NEWEST non-zero price (loop runs newest→
      //     oldest; first hit wins and we stop overwriting). The original
      //     let every iteration overwrite it, so it exited holding the
      //     OLDEST price — a ~clusterWindow-stale event anchor that poisoned
      //     every absorption/markup measurement taken from ev.px.
      const clCut = now - cfg.clusterWindowSec * 1000
      let cBuy = 0, cSell = 0, cLastPx = 0
      for (let i = swaps.length - 1; i >= 0; i--) {
        if (T(swaps[i]) < clCut) break
        const v = V(swaps[i])
        if (swaps[i].side === 'sell') cSell += v; else if (swaps[i].side === 'buy') cBuy += v
        if (cLastPx === 0) { const p = P(swaps[i]); if (p > 0) cLastPx = p }
      }
      if (cLastPx === 0) cLastPx = mcap
      if (cSell >= cfg.clusterSizeX * median) this._openEvent('sell', cLastPx, cSell, now, token, ctx, { kind: 'cluster', x: +(cSell / median).toFixed(1) })
      if (cBuy  >= cfg.clusterSizeX * median) this._openEvent('buy',  cLastPx, cBuy,  now, token, ctx, { kind: 'cluster', x: +(cBuy / median).toFixed(1) })
    }

    // ── G4: evaluate pending events against the CURRENT price ──
    for (let i = this._pending.length - 1; i >= 0; i--) {
      const ev = this._pending[i]
      // track extremes since the event
      if (mcap < ev.lowSince) ev.lowSince = mcap
      if (mcap > ev.highSince) ev.highSince = mcap

      // First-cross time reconstruction (velocity fix): the fire tick may
      // land well after price actually crossed the markup threshold if the
      // strategy loop is coarse, which understated velocity → false TOO_SLOW.
      // Scan THIS tick's fresh swaps for the earliest one that already
      // satisfies the markup and stamp it once. Sub-tick resolution straight
      // off the swap stream, no faster loop required.
      if (!ev.crossT && ev.side === 'sell') {
        for (const sw of newSwaps) {
          const sp = P(sw)
          if (sp > 0 && (sp - ev.px) / ev.px >= cfg.responseMovePct) { ev.crossT = T(sw) || now; break }
        }
      }
      const ageSec = (now - ev.t0) / 1000

      if (ev.side === 'sell') {
        // absorption must HOLD: price trading materially below the event = failed
        if (ev.lowSince < ev.px * (1 - cfg.absorbBreakPct)) {
          this._decline('ABSORB_FAIL', token, ev); this._pending.splice(i, 1); continue
        }
        const movePct = (mcap - ev.px) / ev.px
        if (movePct >= cfg.responseMovePct) {
          // Velocity off the true first-cross age when we have it, else the
          // tick age (still ≥ the real value, so it can only be conservative).
          const crossAge = ev.crossT ? Math.max((ev.crossT - ev.t0) / 1000, 0.001) : Math.max(ageSec, 0.001)
          const vel = movePct / crossAge
          if (vel < cfg.responseVelPctPerSec) { this._decline('TOO_SLOW', token, ev, { movePct, vel }); this._pending.splice(i, 1); continue }
          // RESISTANCE_AHEAD: wall within headroom above the response price
          const wall = ArdDetector.wallMcap(ctx.wallAbove)
          if (wall > 0 && (wall - mcap) / mcap < cfg.headroomPct) {
            this._decline('RESISTANCE_AHEAD', token, ev, { wall }); this._pending.splice(i, 1); continue
          }
          this._pending.splice(i, 1)
          this.onWouldFire?.({
            dir: 'LONG', token, mcap,
            event: { side: ev.side, px: ev.px, sol: +ev.sol.toFixed(2), kind: ev.meta.kind, x: ev.meta.x, strengthX: ev.meta.x },
            gates: { ...this._g1, movePct: +(movePct * 100).toFixed(2), velPctPerSec: +(vel * 100).toFixed(3), ageSec: +crossAge.toFixed(1) },
          })
          continue
        }
        if (ageSec > cfg.responseWindowSec) { this._decline('NO_RESPONSE', token, ev); this._pending.splice(i, 1) }
      } else {
        // MIRROR (buy aggression): price falling on big buying = failed buyers.
        // Phase 1 emits it as a PROTECT warning — the exit half of the spec.
        const dropPct = (ev.px - mcap) / ev.px
        if (dropPct >= cfg.responseMovePct) {
          this._pending.splice(i, 1)
          this.onWouldFire?.({
            dir: 'PROTECT', token, mcap,
            event: { side: ev.side, px: ev.px, sol: +ev.sol.toFixed(2), kind: ev.meta.kind, x: ev.meta.x, strengthX: ev.meta.x },
            gates: { ...this._g1, dropPct: +(dropPct * 100).toFixed(2), ageSec: +ageSec.toFixed(1) },
          })
          continue
        }
        if (ageSec > cfg.responseWindowSec) this._pending.splice(i, 1)  // buyers won; no warning
      }
    }
  }

  _openEvent(side, px, sol, t0, token, ctx, meta) {
    if (side !== 'sell' && side !== 'buy') return
    if (!(px > 0)) return
    // de-dup: ONE event per side per cluster bucket, remembered for
    // dedupKeepMs. This is now the single mechanism that kills BOTH failure
    // modes the shadow stats cared about:
    //   1. a sustained burst re-opening a "new" event every bucket, and
    //   2. a gated-out event (NOT_AT_VALUE etc.) re-declining every tick.
    // The key is set on FIRST processing regardless of pass/fail, so a
    // decline is emitted exactly once per event, not once per tick.
    const key = `${side}:${Math.round(t0 / (this.config.clusterWindowSec * 1000))}`
    if (this._seenKeys.has(key)) return
    this._seenKeys.set(key, t0)

    // Gate G1 (long side only — the mirror is informational either way)
    if (side === 'sell') {
      if (!this._g1?.active)   return this._decline('NOT_ACTIVE', token, { side, px, sol, meta })
      if (!this._g1?.volatile) return this._decline('NOT_VOLATILE', token, { side, px, sol, meta, rangePct: this._g1.rangePct })
      // Gate G2 AT_VALUE: near an HVN of the rolling swap-histogram profile
      if (!this._atValue(px))  return this._decline('NOT_AT_VALUE', token, { side, px, sol, meta })
    }
    if (this._pending.length >= 4) this._pending.shift()
    this._pending.push({ side, px, sol, t0, lowSince: px, highSince: px, crossT: 0, meta })
  }

  // Rolling swap-histogram profile → value nodes. Server-side stand-in for
  // "the visible range": buckets the last profileWindowSec of swaps by price
  // (absolute step anchored to current mcap, ~0.2%) and takes the top-volume
  // fraction as HVNs. FlipZones plug in later as an additional value source
  // (spec G2b) once the runner computes them.
  _buildProfile(swaps, now, mcapNow) {
    const cut = now - this.config.profileWindowSec * 1000
    const step = mcapNow * 0.002
    if (!(step > 0)) return null
    const buckets = new Map()
    for (let i = swaps.length - 1; i >= 0; i--) {
      if (ArdDetector.t(swaps[i]) < cut) break
      const p = ArdDetector.px(swaps[i]); const v = ArdDetector.vol(swaps[i])
      if (!(p > 0) || !(v > 0)) continue
      const b = Math.round(p / step)
      buckets.set(b, (buckets.get(b) || 0) + v)
    }
    if (!buckets.size) return null
    const entries = [...buckets.entries()].sort((a, b) => b[1] - a[1])
    const take = Math.max(1, Math.round(entries.length * this.config.hvnTopFrac))
    return entries.slice(0, take).map(([b]) => b * step)
  }

  _atValue(px) {
    const nodes = this._valueNodes
    if (!nodes || !nodes.length) {
      // No profile yet. Shadow phase (requireValueNode=false) stays OPEN to
      // learn; Phase 2 flips the flag so a missing profile BLOCKS instead of
      // waving the event through exactly when value context is least reliable.
      return !this.config.requireValueNode
    }
    const band = this.config.valueBandPct
    return nodes.some(n => Math.abs(px - n) / n <= band)
  }

  _decline(reason, token, ev, extra = {}) {
    this.onDecline?.({ reason, token, detail: { side: ev.side, px: ev.px, sol: ev.sol, ...(ev.meta || {}), ...extra } })
  }
}

export default ArdDetector